import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {catchError, filter, map, pluck, switchMap, tap} from 'rxjs/operators';
import {ActivatedRoute} from '@angular/router';
import {Cancelable} from 'lodash';
import debounce from 'lodash-es/debounce';
import {BehaviorSubject, combineLatest, Observable, of, Subject, Subscription} from 'rxjs';
import {environment} from '../../../environments/environment';
import {cache, KEY_ENTER, KEY_ESC} from '../../../utils';
import {AnalyticsService} from '../../shared/analytics.service';
import {HttpError} from '../../shared/api.service';
import {AuthService} from '../../shared/auth.service';
import {NavigationService} from '../../shared/navigation.service';
import {ClientApiService} from '../client-api.service';
import {PositionSearchOptions, PositionSearchPipe, PositionStatus} from '../position-search.pipe';
import {QccModalComponent} from '../qcc-modal/qcc-modal.component';
import {QccInfoResponse} from '../../../../app/scripts/services/api';
import {ModalComponent} from '../../shared/modal/modal.component';

const DEFAULT_SORT = {
  open: 'date-desc',
  working: 'action',
  completed: 'end-date'
};

@Component({
  selector: 'positions-page',
  templateUrl: './positions-page.component.html',
  styleUrls: [
    '../styles/paginator.scss',
    '../styles/search-sort.scss',
    '../styles/positions-page.scss',
    './positions-page.component.scss'
  ]
})
export class PositionsPageComponent implements OnInit, OnDestroy {

  @ViewChild('qccModal') qccModal: QccModalComponent;
  @ViewChild('helpModal') helpModal: ModalComponent;

  completedTab = environment.flags.positionsCompletedTab;
  closedTab = environment.flags.positionsClosedTab;
  endOfAssignment = environment.flags.endOfAssignment;

  Status = PositionStatus;
  filterOptions = new Map<string, [string, {}]>([
    ['all', ['positions.filter.all', {}]],
    ['requested', ['positions.filter.requested', {}]],
    ['reviewing', ['positions.filter.reviewing', {}]],
    ['interviewing', ['positions.filter.interviewing', {}]],
    ['offer-extended', ['positions.filter.offer-extended', {}]],
    ['hired', ['positions.filter.hired', {}]]
  ]);
  completedFilterOptions = new Map<string, [string, {}]>([
    ['all-completed', ['positions.filter.all-completed', {}]],
    ['current-year', ['positions.filter.current-year', {}]],
    ['current-quarter', ['positions.filter.current-quarter', {}]],
    ['previous-year', ['positions.filter.previous-year', {}]],
    ['previous-quarter', ['positions.filter.previous-quarter', {}]]
  ]);
  openSortOptions = new Map<string, [string, {}]>([
    ['date-desc', ['positions.sort.date-desc', {}]],
    ['date-asc', ['positions.sort.date-asc', {}]],
    ['action', ['positions.sort.activity', {}]]
  ]);
  workingSortOptions = new Map<string, [string, {}]>([
    ['action', ['positions.sort.activity', {}]],
    ['start-desc', ['positions.sort.start-desc', {}]],
    ['start-asc', ['positions.sort.start-asc', {}]],
    ['end-date-asc', ['positions.sort.end-date-asc', {}]]
  ]);
  completedSortOptions = new Map<string, [string, {}]>([
    ['end-date', ['positions.sort.end-date', {}]],
    ['start-date', ['positions.sort.start-date', {}]],
    ['hired-date', ['positions.sort.hired-date', {}]]
  ]);
  countOptions = new Map<number, [string, {}]>([
    [10, ['pagination.count-option', {number: 10}]],
    [25, ['pagination.count-option', {number: 25}]],
    [50, ['pagination.count-option', {number: 50}]],
    [100, ['pagination.count-option', {number: 100}]]
  ]);

  private defaultSearch: PositionSearchOptions = {
    status: PositionStatus.Open,
    sort: 'date-desc',
    filter: 'all',
    query: null,
    needsAction: false
  };

  qccOrderId$: Observable<number>;
  qccRating$: Observable<number>;
  qccNeedsVerification$: Observable<boolean>;
  qccInfoResponse$: Observable<QccInfoResponse | null>;
  allOrders$: Observable<Order[]>;
  orders$: Observable<Order[]>;
  lowVal$: Observable<number>;
  highVal$: Observable<number>;
  total$: Observable<number>;
  reload$ = new Subject<void>();
  search$ = new BehaviorSubject<PositionSearchOptions>(this.defaultSearch);
  ordersLoading$ = new BehaviorSubject(true);
  qccLoading$ = new BehaviorSubject(false);
  currentPage$ = new BehaviorSubject(1);
  pageSize$ = new BehaviorSubject(10);
  hasSearch$ = new BehaviorSubject(false);
  staff$ = this.Auth.user$.pipe(filter(v => !!v), pluck('staff'));
  loading$ = combineLatest(
    this.ordersLoading$,
    this.qccLoading$,
    (ordersLoading, qccLoading) => (ordersLoading || qccLoading)
  );

  searchText: string;

  private ordersSub: Subscription;
  private paramsSub: Subscription;
  private submitSearchAnalytics: (() => void) & Cancelable;

  constructor(private Api: ClientApiService,
              private Auth: AuthService,
              private route: ActivatedRoute,
              private Navigation: NavigationService,
              private Search: PositionSearchPipe,
              private Analytics: AnalyticsService) {
  }

  ngOnInit() {
    this.initializeQccData();
    this.initializeOrders();
    this.initializePagination();
    this.initializeAnalytics();

    // Trigger initial HTTP request
    this.ordersSub = this.orders$.subscribe();
    this.reload$.next();

    this.paramsSub = this.route.paramMap
      .subscribe(params => {
        this.setStatus(params.get('status') as PositionStatus);
        this.currentPage$.next(1);
      });
  }

  initializeQccData() {
    this.qccRating$ = this.route.queryParams.pipe(pluck('qccRating'));
    this.qccOrderId$ = this.route.queryParams.pipe(
      map(params => parseInt(params.qccOrderId, 10))
    );

    this.qccNeedsVerification$ = this.route.queryParams.pipe(
      pluck('qccNeedsVerification'),
      map(needsVerification => needsVerification === 'true')
    );

    this.qccInfoResponse$ = this.route.queryParams.pipe(
      tap(() => this.qccLoading$.next(true)),
      switchMap(params => this.getQccInfo(params)),
      switchMap(qcc => this.saveInitialQccRating(qcc)),
      tap(() => this.qccLoading$.next(false)),
      catchError((err: HttpError) => this.handleError('Failed to retrieve QCC info', err)),
      cache()
    );
  }

  getQccInfo(params: any): Observable<{response, orderId, rating}> {
    return params.qccTrackingId
      ? this.Api.getQccInfo(params.qccTrackingId).pipe(
        map(response => ({response, orderId: params.qccOrderId, rating: params.qccRating}))
      )
      : of({response: null, orderId: null, rating: null});
  }

  saveInitialQccRating({response, orderId, rating}): Observable<QccInfoResponse> {
    if (response && orderId && rating) {
      const ratedOrder = response.orders.find(order => order.order === parseInt(orderId, 10));

      return this.Api.saveQccReplies(response.token, {
        replies: [{...ratedOrder, rating}],
        companyNotes: response.companyNotes,
        companyRating: response.companyRating
      }, false).pipe(
        tap(ids => ratedOrder.id = ids[0]),
        map(() => response)
      );
    } else {
      return of(null);
    }
  }

  initializeOrders() {
    this.allOrders$ = this.reload$.pipe(
      tap(() => this.ordersLoading$.next(true)),
      switchMap(() => this.Api.getPositions()),
      tap(() => this.ordersLoading$.next(false)),
      catchError((err: HttpError) => this.handleError('Failed to load positions.', err)),
      cache()
    );

    this.orders$ = combineLatest(this.allOrders$, this.search$,
      (orders, search) => this.Search.transform(orders, search))
      .pipe(
        tap(() => this.currentPage$.next(1)),
        cache()
      );
  }

  handleError(message: string, err: HttpError): Observable<null> {
    console.error(message, err);
    this.qccLoading$.next(false);

    return this.Navigation.handleError(err) && of(null);
  }

  initializePagination() {
    this.lowVal$ = combineLatest(this.currentPage$, this.pageSize$,
      (currentPage, pageSize) => ((currentPage - 1) * pageSize) + 1);
    this.highVal$ = combineLatest(this.currentPage$, this.pageSize$, this.orders$,
      (currentPage, pageSize, orders) => Math.min(orders.length, currentPage * pageSize));
    this.total$ = this.orders$.pipe(map(orders => orders.length));
  }

  initializeAnalytics() {
    this.submitSearchAnalytics = debounce(() => {
      if (this.searchText) {
        this.Analytics.trackEvent('Positions: Search', {
          category: 'Action',
          searchText: this.searchText
        });
      }
    }, 2000);
  }

  ngOnDestroy(): void {
    if (this.ordersSub) this.ordersSub.unsubscribe();
    if (this.paramsSub) this.paramsSub.unsubscribe();
    if (this.submitSearchAnalytics) this.submitSearchAnalytics.flush();
  }

  setStatus(status: PositionStatus) {
    this.updateSearch({
      ...this.defaultSearch,
      status,
      sort: DEFAULT_SORT[status],
      filter: status === PositionStatus.Completed ? 'all-completed' : 'all'
    });
  }

  setSort(sort: string): void {
    this.updateSearch({sort});
    this.Analytics.trackEvent('Positions: Sort set to ' + sort);
  }

  setFilter(filter: string): void {
    this.updateSearch({filter});
    this.Analytics.trackEvent('Positions: Filter set to ' + filter);
  }

  setCount(count: number): void {
    this.pageSize$.next(count);
    this.currentPage$.next(1);
  }

  searchTextChanged(): void {
    this.updateSearch({query: this.searchText});
    this.hasSearch$.next(!!this.searchText);
    this.submitSearchAnalytics();
  }

  clearSearch(): void {
    this.searchText = null;
    this.searchTextChanged();
  }

  searchKeydown(event: KeyboardEvent): void {
    if (event.keyCode === KEY_ESC) {
      this.clearSearch();
      event.preventDefault();
    } else if (event.keyCode === KEY_ENTER) {
      (event.target as HTMLElement).blur();
      event.preventDefault();
    }
  }

  toggleNeedsAction() {
    this.updateSearch({needsAction: !this.search.needsAction});
    this.Analytics.trackEvent('Positions: Show only needs action set to ' + this.search.needsAction);
  }

  showHelpModal() {
    this.helpModal.show();
  }

  reload() {
    this.reload$.next();
  }

  get search() {
    return this.search$.getValue();
  }

  private updateSearch(data: Partial<PositionSearchOptions>) {
    this.search$.next({...this.search, ...data});
  }
}
